import React, { useRef, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { FiX } from "react-icons/fi";
import { Button } from "components/ui";
import { useTranslation } from "lib/i18n/use-translation";
import {
  AssessmentQuestionStep,
  SelectInput,
  SelectInputOption,
} from "apps/user/learner/api/types";
import { splitQuestionTitle } from "./questionTitle";
import { CompanyQuestionResponse } from "providers/Assessment";
import styles from "./CompanyQuestionsModal.module.scss";

export interface CompanyQuestionsModalProps {
  questions: AssessmentQuestionStep[];
  onSubmit: (responses: CompanyQuestionResponse[]) => void;
  onClose: () => void;
  isSubmitting?: boolean;
}

// A two-option select renders as a pair of pills rather than a scale. This is
// a structural test on purpose: matching the wording sent a French "Oui"/"Non"
// question down the scale branch.
const isYesNoQuestion = (options: SelectInputOption[]): boolean =>
  options.length === 2;

// Any SelectInput with 3+ options is a scale question.
const isScaleQuestion = (options: SelectInputOption[]): boolean =>
  options.length >= 3;

const isNumberQuestion = (step: AssessmentQuestionStep): boolean =>
  step.input.__typename === "TextInput" && step.input.type === "number";

const defaultAnswerFor = (step: AssessmentQuestionStep): string => {
  if (step.input.__typename === "SelectInput") {
    const options = step.input.options;
    if (isScaleQuestion(options)) {
      // The midpoint of an odd authored scale is its neutral point, so
      // position gives the default without reading any of the wording.
      const fallback = options[Math.floor(options.length / 2)];
      return fallback?.id ?? "";
    }
    return "";
  }
  if (isNumberQuestion(step)) {
    return "0";
  }
  return "";
};

const responseFor = (
  step: AssessmentQuestionStep,
  rawAnswer: string
): CompanyQuestionResponse => {
  if (step.input.__typename === "SelectInput") {
    const options = (step.input as SelectInput).options;
    const option = options.find((o) => o.id === rawAnswer);
    return {
      stepId: step.id,
      score: option?.value ? parseInt(option.value, 10) || 0 : 0,
      optionId: option?.id,
    };
  }

  return {
    stepId: step.id,
    text: rawAnswer,
    score: parseInt(rawAnswer, 10) || 0,
  };
};

const YesNoInput: React.FC<{
  options: SelectInputOption[];
  value: string;
  onChange: (label: string) => void;
}> = ({ options, value, onChange }) => {
  const sorted = [...options].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );

  return (
    <div className={styles["pill-row"]}>
      {sorted.map((option) => (
        <button
          key={option.id ?? option.value}
          type="button"
          className={
            value === option.id ? styles["pill-active"] : styles.pill
          }
          onClick={() => onChange(option.id ?? "")}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

const ScaleInput: React.FC<{
  options: SelectInputOption[];
  value: string;
  onChange: (label: string) => void;
}> = ({ options, value, onChange }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const selectedIndex = options.findIndex((option) => option.id === value);
  const selectedOption = options[selectedIndex];

  const indexFromClientX = (clientX: number): number => {
    const track = trackRef.current;
    if (!track) return selectedIndex;
    const { left, width } = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - left) / width));
    return Math.round(ratio * (options.length - 1));
  };

  const handlePointerMove = (event: PointerEvent) => {
    const index = indexFromClientX(event.clientX);
    const option = options[index];
    if (option && option.id !== value) {
      onChange(option.id ?? "");
    }
  };

  const handlePointerUp = () => {
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    handlePointerMove(event.nativeEvent);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  const selectedPercent =
    options.length > 1 ? (selectedIndex / (options.length - 1)) * 100 : 0;
  // Center the label under its marker, but pin it to the track's edges
  // instead of overflowing when the selection is the first/last option.
  const labelTransform =
    selectedPercent <= 0
      ? "translateX(0)"
      : selectedPercent >= 100
      ? "translateX(-100%)"
      : "translateX(-50%)";

  return (
    <div className={styles["scale-input"]} data-testid="company-question-scale">
      <div
        ref={trackRef}
        className={styles["scale-track"]}
        data-testid="company-question-scale-track"
        onPointerDown={handlePointerDown}
      >
        <div className={styles["scale-track-line"]} />
        {options.map((option, index) => (
          <button
            key={option.id ?? option.value}
            type="button"
            aria-label={option.label}
            className={
              index === selectedIndex
                ? styles["scale-marker-active"]
                : styles["scale-marker"]
            }
            onClick={() => onChange(option.id ?? "")}
          />
        ))}
      </div>
      <p
        className={styles["scale-label"]}
        style={{ left: `${selectedPercent}%`, transform: labelTransform }}
      >
        {selectedOption?.label}
      </p>
    </div>
  );
};

const NumberInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation(["assessment"]);
  const count = parseInt(value, 10) || 0;

  return (
    <div className={styles.counter} data-testid="company-question-counter">
      <button
        type="button"
        aria-label="decrease"
        className={styles["counter-button"]}
        disabled={count <= 0}
        onClick={() => onChange(String(Math.max(0, count - 1)))}
      >
        {"−"}
      </button>
      <div className={styles["counter-value-wrap"]}>
        <p className={styles["counter-value"]}>{count}</p>
        <p className={styles["counter-unit"]}>
          {t("assessment:companyModal.daysUnit")}
        </p>
      </div>
      <button
        type="button"
        aria-label="increase"
        className={styles["counter-button"]}
        onClick={() => onChange(String(count + 1))}
      >
        {"+"}
      </button>
    </div>
  );
};

const CompanyQuestionsModal: React.FC<CompanyQuestionsModalProps> = ({
  questions,
  onSubmit,
  onClose,
  isSubmitting = false,
}) => {
  const { t } = useTranslation(["assessment"]);
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    questions.forEach((question) => {
      initial[question.id] = defaultAnswerFor(question);
    });
    return initial;
  });

  const setAnswer = (stepId: string, value: string) =>
    setAnswers((prev) => ({ ...prev, [stepId]: value }));

  const handleSubmit = () => {
    const responses = questions.map((question) =>
      responseFor(question, answers[question.id] ?? "")
    );
    onSubmit(responses);
  };

  return (
    <Modal
      isOpen={true}
      className={styles.modal}
      contentClassName={styles["modal-content"]}
    >
      <button
        type="button"
        aria-label={t("assessment:companyModal.closeLabel")}
        className={styles["close-button"]}
        onClick={onClose}
      >
        <FiX size={20} />
      </button>

      <ModalBody className={styles.body}>
        <h1 className={styles.heading}>
          {t("assessment:companyModal.title")}
        </h1>

        <div className={styles.questions}>
          {questions.map((question) => {
            const { title } = splitQuestionTitle(question.titleHtml);
            const options =
              question.input.__typename === "SelectInput"
                ? question.input.options
                : [];
            const isYesNo =
              question.input.__typename === "SelectInput" &&
              isYesNoQuestion(options);
            const isScale =
              question.input.__typename === "SelectInput" &&
              isScaleQuestion(options);

            return (
              <div key={question.id} className={styles.question}>
                <p className={styles["question-title"]}>{title}</p>

                {isYesNo && (
                  <YesNoInput
                    options={options}
                    value={answers[question.id] ?? ""}
                    onChange={(label) => setAnswer(question.id, label)}
                  />
                )}

                {isScale && (
                  <ScaleInput
                    options={options}
                    value={answers[question.id] ?? ""}
                    onChange={(label) => setAnswer(question.id, label)}
                  />
                )}

                {isNumberQuestion(question) && (
                  <NumberInput
                    value={answers[question.id] ?? "0"}
                    onChange={(value) => setAnswer(question.id, value)}
                  />
                )}
              </div>
            );
          })}
        </div>

        <Button
          variant="enclosed-primary"
          monochromeColour="black"
          className={styles["submit-button"]}
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {t("assessment:companyModal.submit")}
        </Button>
      </ModalBody>
    </Modal>
  );
};

export default CompanyQuestionsModal;
